import {
  AHText,
  AHTextFonts,
  AHIcon,
  Box,
  useLayoutContext,
  EmployeeAvatar,
  useCircleSize,
} from "arkh";
import { Avatar_employee$key } from "arkh/src/cross-platform/Actor/__generated__/Avatar_employee.graphql";
import { View } from "react-native";
import { graphql, useFragment } from "react-relay";

import { ListItemRoles_roles$key } from "./__generated__/ListItemRoles_roles.graphql";
import {
  isLinkedToViewer,
  isViewerEmployee,
  prioritizeEmployees,
  prioritizeRoles,
} from "./OverlappingEmployeeAvatars";
import { useCurrentTeamId } from "../../CurrentTeamIdContext";

export type ListItemRoleEmployee = {
  readonly id: string;
  readonly name: string;
  readonly isLinkedToViewer: boolean;
  readonly workStatus: string;
} & Avatar_employee$key;

export type ListItemRole = {
  id: string;
  name: string | null | undefined;
  isLinkedToViewer: boolean;
  employeeIds: readonly string[];
  employees?: readonly ListItemRoleEmployee[];
};

export function isCurrentEmployeeInRole(
  role: ListItemRole,
  currentEmployeeId: string | undefined
): boolean {
  return isLinkedToViewer(role, currentEmployeeId, role.employeeIds);
}

export type ListItemRolesProps = {
  roles: ListItemRoles_roles$key;
  currentEmployeeId?: string | undefined;
  maxShowingRoles: number;
  highlightViewerRoles: boolean;
  textType?: "body" | "detail";
  spacing?: "xsmall" | "small";
};

export function ListItemRoles(props: ListItemRolesProps) {
  const { space } = useLayoutContext();
  const currentTeamId = useCurrentTeamId();

  const roles = useFragment(
    graphql`
      fragment ListItemRoles_roles on EmployeeRole @relay(plural: true) {
        id
        name
        isLinkedToViewer
        employeeIds
        team {
          id
        }
        employees {
          id
          name
          isLinkedToViewer
          workStatus
          ...Avatar_employee
        }
      }
    `,
    props.roles
  );

  if (roles.length === 0) {
    return null;
  }

  const prioritized = prioritizeRoles(roles, props.currentEmployeeId);
  const sortedRoles = currentTeamId
    ? [...prioritized].sort((a, b) => {
        const aCurrentTeam = a.team.id === currentTeamId ? 1 : 0;
        const bCurrentTeam = b.team.id === currentTeamId ? 1 : 0;
        return bCurrentTeam - aCurrentTeam;
      })
    : prioritized;
  const visibleRoles = sortedRoles.slice(0, props.maxShowingRoles);

  return (
    <View
      style={{ flexDirection: "row", alignItems: "center", gap: space[props.spacing ?? "small"] }}
    >
      {visibleRoles.map((role) => {
        const isHighlighted = isCurrentEmployeeInRole(
          role,
          props.currentEmployeeId
        );
        return (
          <RoleChip
            key={role.id}
            role={role}
            isHighlighted={props.highlightViewerRoles && isHighlighted}
            dimWhenNotHighlighted={props.highlightViewerRoles}
            textType={props.textType}
            spacing={props.spacing}
            currentEmployeeId={props.currentEmployeeId}
            highlightViewer={props.highlightViewerRoles}
          />
        );
      })}
    </View>
  );
}

export function RoleChip(props: {
  role: ListItemRole;
  isHighlighted: boolean;
  dimWhenNotHighlighted: boolean;
  textType?: "body" | "detail";
  spacing?: "xsmall" | "small";
  currentEmployeeId?: string;
  highlightViewer: boolean;
}) {
  const { role, isHighlighted, dimWhenNotHighlighted, textType } = props;
  const employees = role.employees ?? [];

  const textColor =
    !dimWhenNotHighlighted || isHighlighted ? "text-primary" : "text-secondary";

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {employees.length > 0 ? (
        <StackedRoleAvatars
          employees={employees}
          highlightViewer={props.highlightViewer}
          currentEmployeeId={props.currentEmployeeId}
        />
      ) : (
        <AHIcon
          type="employee-role"
          color="text-secondary"
          size={textType === "detail" ? "small" : undefined}
        />
      )}
      <Box spaceLeft={props.spacing ?? "small"}>
        <AHText
          type={textType ?? "body"}
          font={textType === "body" ? AHTextFonts.MEDIUM : undefined}
          color={textColor}
          numberOfLines={1}
        >
          {role.name}
        </AHText>
      </Box>
    </View>
  );
}

/**
 * Shows a single avatar with an optional second avatar behind at lower opacity
 * to indicate there are more employees in the role.
 */
function StackedRoleAvatars(props: {
  employees: readonly ListItemRoleEmployee[];
  highlightViewer: boolean;
  currentEmployeeId?: string;
}) {
  const { employees, highlightViewer, currentEmployeeId } = props;
  const size = useCircleSize("smallMedium");

  const prioritized = prioritizeEmployees(employees, currentEmployeeId);
  const frontEmployee = prioritized[0];
  const backEmployee = prioritized.length > 1 ? prioritized[1] : null;

  const isFrontViewer = frontEmployee
    ? isViewerEmployee(frontEmployee, currentEmployeeId)
    : false;

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {backEmployee ? (
        <View style={{ opacity: 0.4, marginRight: -size * 0.6 }}>
          <EmployeeAvatar
            employee={backEmployee}
            size="small"
            style="default"
          />
        </View>
      ) : null}
      {frontEmployee ? (
        <EmployeeAvatar
          employee={frontEmployee}
          size="small"
          style={
            highlightViewer && isFrontViewer ? "assigned-to-me" : "default"
          }
        />
      ) : null}
    </View>
  );
}
